import { msg } from "@lingui/core/macro";
import { createRoute, useSearch } from "@tanstack/solid-router";
import { Link, useNavigate } from "@/lib/u-router";
import { createEffect, createMemo, createSignal, For, onMount, Show } from "solid-js";
import { Boxes, Download, History, Package, Pencil, Trash2 } from "lucide-solid";
import {
  buildProductDataset,
  createProductMutation,
  deleteProductMutation,
  exportDatasetAsCsv,
  exportDatasetAsPdf,
  getProductsQuery,
  queryKeys,
  restoreProductMutation,
  updateProductMutation,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { PageHeader } from "@/components/ui/page-header";
import { PageContainer } from "@/components/ui/page-container";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { EmptyShelvesIllustration } from "@/components/ui/illustrations";
import { Select } from "@/components/ui/select";
import { SwipeRow } from "@/components/ui/swipe-row";
import { showToast } from "@/components/ui/toast";
import { useUndo } from "@/lib/undo";
import { EntityHistoryDialog } from "@/features/activity/components/entity-history-dialog";
import type { ProductRecord } from "@stockpilot/powersync";

function ProductsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();
  const search = useSearch({ from: "/u/$u/products" });
  const navigate = useNavigate();

  const [name, setName] = createSignal("");
  let nameInput: HTMLInputElement | undefined;
  const [sku, setSku] = createSignal("");
  const [tracking, setTracking] = createSignal("none");
  const [editName, setEditName] = createSignal("");

  // El producto en edición es deep-linkable: la URL (`?edit=<id>`) es la fuente de
  // verdad del modo edición de esa fila. Pegar/compartir `/products?edit=<id>`
  // reabre exactamente esa fila en edición; recargar lo reconstruye (idempotente).
  // `setEditingId` solo escribe la URL.
  const editingId = (): string | null => search().edit ?? null;
  const setEditingId = (id: string | null) => {
    void navigate({
      to: "/products",
      search: id ? { edit: id } : {},
      replace: true,
    });
  };

  // Guard del flush diferido del router: si el boot perdió el `?edit=` del
  // deep-link inicial, lo re-aplicamos síncronamente desde el snapshot.
  onMount(() => {
    const initialEdit = takeInitialSearchParam("edit", "/products");
    if (initialEdit && !editingId()) {
      setEditingId(initialEdit);
    }
  });

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const editingProduct = createMemo(() => (products.data ?? []).find((p) => p.id === editingId()));

  // Seed del nombre editable cuando entra en modo edición vía URL/deep-link (la
  // fila puede no haber estado en pantalla cuando se escribió `editName`).
  let lastSeededId: string | null = null;
  createEffect(() => {
    const prod = editingProduct();
    if (prod && prod.id !== lastSeededId) {
      lastSeededId = prod.id;
      setEditName(prod.name ?? "");
    }
    if (!editingId()) {
      lastSeededId = null;
    }
  });

  // Resuelve el producto deep-linkeado en edición (`?edit=<id>`): presente /
  // cargando / no-encontrado / offline. Nunca deja un loader colgado.
  const editResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(editingProduct()),
    isPending: () => products.isLoading,
  });

  const { registerUndo } = useUndo();
  // Sheet "Historial" (ACTIVITY-CORE) del producto seleccionado.
  const [historyId, setHistoryId] = createSignal<string | null>(null);

  // K1 — Export del catálogo visible a CSV / PDF (adapter en @stockpilot/shared;
  // los serializadores son genéricos, solo el adapter conoce el dominio).
  const handleExport = (format: "csv" | "pdf") => {
    const dataset = buildProductDataset(products.data ?? [], {
      title: t(msg`Products`),
      subtitle: t(msg`Exported product catalog`),
    });
    if (format === "csv") {
      exportDatasetAsCsv(dataset);
    } else {
      exportDatasetAsPdf(dataset);
    }
  };

  async function handleCreate(e: Event) {
    e.preventDefault();
    const value = name().trim();
    const d = drizzle();
    if (!value || !d) {
      return;
    }
    await createProductMutation(d, orgId(), {
      name: value,
      sku: sku().trim() || null,
      tracking: tracking(),
    });
    setName("");
    setSku("");
    setTracking("none");
    showToast(t(msg`Product saved`), "success");
  }

  async function handleSaveEdit(id: string) {
    const value = editName().trim();
    const d = drizzle();
    if (!value || !d) {
      return;
    }
    await updateProductMutation(d, id, { name: value });
    setEditingId(null);
    showToast(t(msg`Changes saved`), "success");
  }

  async function handleDelete(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    // L2: soft-delete → papelera. L1: toast "Deshacer" restaura al instante.
    await deleteProductMutation(d, id);
    registerUndo(t(msg`Product moved to trash`), async () => {
      await restoreProductMutation(d, id);
    });
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Products`)}
        description={t(msg`Your catalog of items, SKUs and barcodes.`)}
        icon={Package}
      />

      <form class="mt-6 flex flex-wrap gap-2" onSubmit={handleCreate} data-testid="product-form">
        <input
          class="flex-1 min-w-40 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
          placeholder={t(msg`Product name`)}
          value={name()}
          ref={nameInput}
          onInput={(e) => setName(e.currentTarget.value)}
          data-testid="product-name-input"
        />
        <input
          class="w-32 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
          placeholder={t(msg`SKU`)}
          value={sku()}
          onInput={(e) => setSku(e.currentTarget.value)}
          data-testid="product-sku-input"
        />
        <Select
          class="w-40"
          value={tracking()}
          onChange={(v) => setTracking(v)}
          data-testid="product-tracking-select"
          aria-label={t(msg`Tracking`)}
          options={[
            { value: "none", label: t(msg`No tracking`) },
            { value: "lot", label: t(msg`By lot`) },
            { value: "serial", label: t(msg`By serial`) },
          ]}
        />
        <Button type="submit" disabled={!drizzle()} data-testid="product-add-button">
          {t(msg`Add product`)}
        </Button>
      </form>

      {/* Deep-link de edición no resoluble: la URL pide editar un producto que no
          existe / sin acceso / offline. Feedback en lugar de no abrir nada. */}
      <Show when={editingId() && editResolution() !== "ready" && editResolution() !== "loading"}>
        <div class="mt-6" data-testid="product-edit-not-found">
          <NotFoundState
            variant={editResolution() === "offline" ? "offline" : "not-found"}
            title={editResolution() === "offline" ? undefined : t(msg`Product not found`)}
            description={
              editResolution() === "offline"
                ? undefined
                : t(msg`This product may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to products`),
              onClick: () => {
                setEditingId(null);
              },
            }}
          />
        </div>
      </Show>

      {/* K1: export del catálogo (solo con datos; el dataset vacío no aporta). */}
      <Show when={(products.data ?? []).length > 0}>
        <div class="mt-6 flex items-center justify-end gap-2">
          <span class="mr-auto text-xs text-muted-foreground">
            {t(msg`Export the catalog below`)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleExport("csv")}
            data-testid="export-csv-button"
          >
            <Download class="h-4 w-4" />
            CSV
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleExport("pdf")}
            data-testid="export-pdf-button"
          >
            <Download class="h-4 w-4" />
            PDF
          </Button>
        </div>
      </Show>

      <div class="mt-3" data-testid="product-list">
        <Show
          when={(products.data ?? []).length > 0}
          fallback={
            <EmptyState
              icon={Package}
              illustration={EmptyShelvesIllustration}
              title={t(msg`No products yet`)}
              description={t(msg`Add your first product to start tracking stock.`)}
              onIconActivate={() => nameInput?.focus()}
              iconActionLabel={t(msg`Add your first product`)}
            />
          }
        >
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={products.data ?? []}>
              {(p, i) => (
                <li
                  class="list-row-in"
                  style={{ "--row-index": Math.min(i(), 12) }}
                  data-testid="product-row"
                  data-product-id={p.id}
                  data-product-name={p.name}
                >
                  <SwipeRow
                    disabled={editingId() === p.id}
                    right={{
                      icon: Pencil,
                      label: t(msg`Edit`),
                      tone: "primary",
                      onCommit: () => {
                        setEditingId(p.id);
                        setEditName(p.name ?? "");
                      },
                    }}
                    left={{
                      icon: Trash2,
                      label: t(msg`Delete`),
                      tone: "danger",
                      exits: true,
                      onCommit: () => void handleDelete(p.id),
                    }}
                  >
                    <div class="flex items-center gap-3 px-4 py-3">
                      <Show
                        when={editingId() === p.id}
                        fallback={
                          <>
                            <Package class="h-5 w-5 text-primary shrink-0" />
                            <div class="flex-1 min-w-0">
                              <p class="font-medium truncate" data-testid="product-row-name">
                                {p.name}
                              </p>
                              <Show when={p.sku}>
                                <p class="text-xs text-muted-foreground">{p.sku}</p>
                              </Show>
                            </div>
                            {/* Interconnect: jump to this product's stock. */}
                            <Link
                              to="/stock"
                              search={{ product: p.id }}
                              class="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
                              title={t(msg`View stock for this product`)}
                              data-testid="product-view-stock"
                            >
                              <Boxes class="h-4 w-4" />
                              <span class="max-sm:hidden">{t(msg`Stock`)}</span>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setHistoryId(p.id)}
                              title={t(msg`History`)}
                              data-testid="product-history-button"
                            >
                              <History class="h-4 w-4" />
                              <span class="max-sm:hidden">{t(msg`History`)}</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingId(p.id);
                                setEditName(p.name ?? "");
                              }}
                              data-testid="product-edit-button"
                            >
                              {t(msg`Edit`)}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void handleDelete(p.id)}
                              data-testid="product-delete-button"
                            >
                              {t(msg`Delete`)}
                            </Button>
                          </>
                        }
                      >
                        <input
                          class="flex-1 h-9 rounded-md border border-border bg-field-background px-3 text-sm"
                          value={editName()}
                          onInput={(e) => setEditName(e.currentTarget.value)}
                          data-testid="product-edit-input"
                        />
                        <Button
                          size="sm"
                          onClick={() => void handleSaveEdit(p.id)}
                          disabled={!drizzle()}
                          data-testid="product-save-button"
                        >
                          {t(msg`Save`)}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                          {t(msg`Cancel`)}
                        </Button>
                      </Show>
                    </div>
                  </SwipeRow>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>

      {/* Historial por entidad (ACTIVITY-CORE): cada cambio, con actor y revert. */}
      <Show when={historyId()}>
        {(id) => (
          <EntityHistoryDialog
            open={Boolean(historyId())}
            onOpenChange={(open) => {
              if (!open) {
                setHistoryId(null);
              }
            }}
            entityType="product"
            entityId={id()}
          />
        )}
      </Show>
    </PageContainer>
  );
}

export const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products",
  validateSearch: (search: Record<string, unknown>): { edit?: string } => ({
    edit: typeof search.edit === "string" && search.edit.trim() !== "" ? search.edit : undefined,
  }),
  component: ProductsPage,
});
